// SelectionBreadcrumb — drill path in the header (US-878 · R-7).
//
// Shows `Asia-Pacific › Country › Province › District` for the active selection.
// Every crumb except the current (last) one is clickable to drill UP to that
// level; the root drills all the way out to the APAC view. A Clear action (×)
// resets the selection — restoring the APAC framing and unfiltered layers. Sits
// in the navy header, so text is light; hidden on phones to keep the bar uncrowded
// (the rail/panel — and their Selection Level control — live in the menu drawer).
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import useSelection from "../../hooks/useSelection";

const CRUMB_SX = {
  fontSize: 12.5,
  fontWeight: 500,
  maxWidth: 160,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const SelectionBreadcrumb = () => {
  const { crumbs, selection, drillUp, clearSelection } = useSelection();
  const lastIndex = crumbs.length - 1;

  return (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        flex: 1,
        ml: 2,
      }}
    >
      <Breadcrumbs
        aria-label="Selection drill-down"
        separator={
          <NavigateNextRoundedIcon
            sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)" }}
          />
        }
        sx={{ minWidth: 0, "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" } }}
      >
        {crumbs.map((crumb, index) =>
          index === lastIndex ? (
            <Typography
              key={crumb.key}
              aria-current="page"
              sx={{ ...CRUMB_SX, fontWeight: 600, color: "primary.contrastText" }}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={crumb.key}
              component="button"
              type="button"
              underline="hover"
              onClick={() => drillUp(crumb.level)}
              sx={{
                ...CRUMB_SX,
                color: "rgba(255,255,255,0.82)",
                "&:hover": { color: "primary.contrastText" },
              }}
            >
              {crumb.label}
            </Link>
          )
        )}
      </Breadcrumbs>

      {selection && (
        <Button
          size="small"
          startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={clearSelection}
          sx={{
            textTransform: "none",
            fontSize: 12,
            color: "rgba(255,255,255,0.82)",
            minWidth: 0,
            px: 1,
            "&:hover": {
              color: "primary.contrastText",
              bgcolor: "rgba(255,255,255,0.08)",
            },
          }}
        >
          Clear
        </Button>
      )}
    </Box>
  );
};

export default SelectionBreadcrumb;
